import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, FileText, Scale, ShieldCheck, Sparkles, ArrowRight, X, TrendingUp } from "lucide-react";

const ARTICLES = [
  {
    icon: Scale,
    category: "Aanbesteden",
    title: "Hoe werkt Flextender voor overheidsorganisaties?",
    excerpt:
      "Flextender is een Managed Service Provider (MSP) die overheden ondersteunt bij het rechtmatig inhuren van flexibel personeel. Hier leest u stap voor stap hoe het werkt.",
    readTime: "4 min lezen",
    sections: [
      {
        h: "Wat is Flextender precies?",
        p:
          "Flextender is géén aanbestedingsplatform, maar een onafhankelijke broker voor de publieke sector. Omdat overheden volgens de Aanbestedingswet flexibel personeel via een openbare procedure moeten inhuren, faciliteert Flextender deze procedure zodat opdrachtgevers geen eigen kandidaten introduceren en de markt eerlijk wordt benaderd.",
      },
      {
        h: "Het proces in vier stappen",
        p:
          "1) De overheidsorganisatie stuurt een opdracht naar Flextender met functie-eisen, uren en periode. 2) Flextender publiceert de uitvraag in de open markt (platform, e-mail, LinkedIn, netwerk van circa 45.000 accounts). 3) Op basis van een vaste beoordelingssystematiek selecteert Flextender de best matchende kandidaten. 4) De opdrachtgever kiest uit de gepresenteerde shortlist en Flextender regelt contract, administratie en facturatie.",
      },
      {
        h: "Waarom kiezen overheden voor Flextender?",
        p:
          "Rechtmatigheid (voldoet aan de Aanbestedingswet), transparantie (eerlijke en onafhankelijke selectie), efficiëntie (geen eigen marktonderzoek nodig) en de matchratio van circa 98%. Voor openbare aanbestedingen zelf wordt TenderNed gebruikt, niet Flextender.",
      },
      {
        h: "De rol van een detacheringsbureau",
        p:
          "Overheidskrachten dient kandidaten in op Flextender-opdrachten en zorgt dat zowel knock-out criteria als gunningscriteria zorgvuldig worden ingevuld. Dat scheelt professionals tijd en verhoogt de kans op een match aanzienlijk.",
      },
    ],
  },
  {
    icon: FileText,
    category: "Omgevingswet",
    title: "Status Omgevingswet 2026: waar staan gemeenten?",
    excerpt:
      "Technisch werkt het hoofdspoor stabiel, maar de echte uitdaging zit in cultuur, ambitie en het halen van de transitietermijnen. Een actueel overzicht.",
    readTime: "5 min lezen",
    sections: [
      {
        h: "Het hoofdspoor werkt",
        p:
          "Sinds Q3 2025 hebben ruim 323 gemeenten een omgevingsdocument via de STOP 1.3-standaard bekendgemaakt. Het Digitaal Stelsel Omgevingswet (DSO) verwerkt vergunningaanvragen, meldingen en informatieplichten in stijgende aantallen. Plansoftwareleveranciers hebben grote stappen gezet.",
      },
      {
        h: "Belangrijke deadlines",
        p:
          "Waterschapsverordening was vastgesteld per 1 januari 2026. De omgevingsvisie van gemeenten moet uiterlijk 1 januari 2027 zijn vastgesteld. Voor het omgevingsplan geldt een transitietermijn tot 1 januari 2032, waarin gemeenten alle bestemmingsplannen moeten omzetten naar het nieuwe omgevingsplan.",
      },
      {
        h: "Cultuur en ambitie als knelpunt",
        p:
          "De techniek is niet de bottleneck, de menselijke kant wel. Veel gemeenten ervaren het DSO in de praktijk complexer dan verwacht en richten zich vooral op compliance in plaats van het maximaal benutten van de kansen die de wet biedt. Het ambitieniveau daalt licht omdat men eerst op basisniveau begint.",
      },
      {
        h: "Wat dit betekent voor inhuur",
        p:
          "Gemeenten vragen versterking op senior beleidsadviseurs Omgevingswet, juridische experts (bezwaarprocedures), DSO-specialisten en projectmanagers die zowel de inhoudelijke als de digitale transitie begrijpen. De vraag stijgt richting de deadlines van 2027 en 2032.",
      },
    ],
  },
  {
    icon: ShieldCheck,
    category: "Privacy & AVG",
    title: "AVG-checklist voor inhuur van externe professionals",
    excerpt:
      "Welke gegevens mag u delen, hoe lang bewaart u een cv en wanneer is een verwerkersovereenkomst nodig? Een praktische checklist voor inleners.",
    readTime: "3 min lezen",
    sections: [
      {
        h: "Welke gegevens mag een inlener ontvangen?",
        p:
          "Alleen gegevens die strikt noodzakelijk zijn voor de matching: cv, expertise, beschikbaarheid en gewenst tarief of salaris. NAW-gegevens en contactgegevens worden pas gedeeld nadat de kandidaat hier expliciet toestemming voor heeft gegeven (artikel 6 AVG, lid 1 sub a).",
      },
      {
        h: "Bewaartermijn",
        p:
          "Sollicitatiegegevens worden in principe maximaal vier weken na afronding van de procedure verwijderd. Met expliciete toestemming van de kandidaat is een bewaartermijn tot één jaar toegestaan, zodat u bij een nieuwe vacature opnieuw contact kunt opnemen.",
      },
      {
        h: "Verwerkersovereenkomst",
        p:
          "Wanneer u een detacheringsbureau inschakelt dat namens u kandidaatgegevens verwerkt, is een verwerkersovereenkomst (VWO) verplicht onder artikel 28 AVG. Hierin staan onder andere het doel, de beveiligingsmaatregelen en de meldplicht bij datalekken vastgelegd.",
      },
      {
        h: "Rechten van de kandidaat",
        p:
          "Iedere kandidaat heeft recht op inzage, rectificatie en verwijdering van zijn of haar gegevens, en het recht om bezwaar te maken tegen verwerking. Zorg dat er een duidelijk contactpunt is voor AVG-verzoeken (vaak de Functionaris Gegevensbescherming).",
      },
    ],
  },
  {
    icon: Sparkles,
    category: "ZZP",
    title: "Wet DBA in 2026: ZZP of detacheren bij de overheid?",
    excerpt:
      "Sinds 1 januari 2025 handhaaft de Belastingdienst de wet DBA weer volledig. Wat betekent dat voor overheidsinleners en wat is het verschil met detacheren?",
    readTime: "6 min lezen",
    sections: [
      {
        h: "Volledige handhaving sinds januari 2025",
        p:
          "Het handhavingsmoratorium dat sinds 2016 gold is per 1 januari 2025 beëindigd. De Belastingdienst kan sindsdien direct naheffingsaanslagen loonheffing en correctieverplichtingen opleggen, zonder voorafgaande waarschuwing. Bij kwaadwillendheid (opzet) is naheffing tot vijf jaar terug mogelijk.",
      },
      {
        h: "Schijnzelfstandigheid: het belangrijkste risico",
        p:
          "De Belastingdienst kijkt naar gezagsrelatie (wie bepaalt hoe en wanneer het werk wordt gedaan?), ondernemersrisico (loopt de ZZP'er zelf commercieel risico?) en het hebben van meerdere opdrachtgevers. Bij langlopende overheidsopdrachten met vaste werktijden en directe supervisie is het risico op herkwalificatie groot.",
      },
      {
        h: "Wat zijn de consequenties?",
        p:
          "Voor de opdrachtgever: naheffing loonheffing, correctie sociale premies (AOW, WIA), eventuele vergrijpboetes vanaf 2026. Voor de ZZP'er: verlies van zelfstandigenaftrek, mkb-winstvrijstelling, en mogelijk recht op pensioenopbouw en ontslagbescherming.",
      },
      {
        h: "Waarom detacheren vaak veiliger is",
        p:
          "Bij detachering is de professional in vaste dienst bij het detacheringsbureau. Het bureau draagt het werkgeversrisico, draagt loonheffing en sociale premies af en heeft een verwerkersovereenkomst. De inlener loopt geen risico op naheffing in het kader van de wet DBA. Voor langlopende of cruciale opdrachten bij de overheid is dit veelal de verstandigste route.",
      },
    ],
  },
  {
    icon: BookOpen,
    category: "Carrière",
    title: "5 tips om op te vallen bij overheidsrecruiters",
    excerpt:
      "Een sterk cv, scherpe motivatie en de juiste keywords. Onze recruiters delen wat het verschil maakt tussen een voorgestelde en een afgewezen kandidaat.",
    readTime: "4 min lezen",
    sections: [
      {
        h: "1. Schrijf je cv op de knock-out criteria",
        p:
          "Flextender en directe overheidsopdrachten werken met knock-out criteria (KO) en gunningscriteria (GC). KO is bindend: zonder KO geen plek op de shortlist. Benoem expliciet relevante werkervaring (jaren, soort overheid, vergelijkbare projecten) in de eerste alinea van je cv.",
      },
      {
        h: "2. Maak je sectorkennis zichtbaar",
        p:
          "Recruiters herkennen overheidservaring aan keywords: Common Ground, NORA, Omgevingswet, Wpg, BIO, AVG, Aanbestedingswet, GR Sociaal Domein, Wmo, Participatiewet. Zorg dat de relevante termen voor jouw expertisegebied prominent in je cv staan.",
      },
      {
        h: "3. Beschikbaarheid en tarief expliciet vermelden",
        p:
          "Een vacature waarvoor je 'binnen 2 weken beschikbaar' bent, levert direct voorrang op. Zelfde voor een realistisch tarief binnen de bandbreedte. Onrealistisch hoog of veel onder de markt zit kost je de opdracht.",
      },
      {
        h: "4. Motivatie in maximaal 5 zinnen",
        p:
          "Begin met waarom je deze specifieke opdracht wilt (organisatie, opgave), gevolgd door één concrete resultaat uit een vergelijkbare rol. Daarna één zin over je werkstijl en één over wat je toevoegt aan het team. Geen algemene clichés.",
      },
      {
        h: "5. Houd je profiel actueel",
        p:
          "Inclusief beschikbaarheidsdatum, geactualiseerd cv en huidige expertisegebieden. Het scheelt onze recruiters tijd en jou een gemiste matchmail.",
      },
    ],
  },
  {
    icon: TrendingUp,
    category: "Tarieven",
    title: "Actuele tariefbenchmark interim publieke sector 2026",
    excerpt:
      "Wat is de range voor een senior beleidsadviseur, ICT-architect of jurist bij gemeenten en rijksoverheid? Een marktanalyse op basis van actuele opdrachten.",
    readTime: "3 min lezen",
    sections: [
      {
        h: "Bandbreedtes per functiegroep (bruto, per uur)",
        p:
          "Senior beleidsadviseur gemeente/rijk: € 90 tot € 130. ICT-architect of senior adviseur informatiebeveiliging: € 100 tot € 150. Jurist of adviseur audit & risk: € 100 tot € 140. Gecombineerde rollen (juridisch + beleid + data): € 120 tot € 160.",
      },
      {
        h: "Verschil rijksoverheid en lokale overheid",
        p:
          "Ministeries en grote uitvoeringsorganisaties (UWV, Belastingdienst, DUO) hanteren vaak hogere maxtarieven (€ 110 tot € 150) en kennen voor specialistische rollen geen maxtarief. Lokale gemeenten zitten meestal in de range € 85 tot € 130, afhankelijk van grootte en complexiteit.",
      },
      {
        h: "Detacheren of ZZP: tariefverschil",
        p:
          "Bij ZZP-opdrachten worden vaak 10 tot 20% hogere tarieven gehanteerd dan bij detachering, omdat de ZZP'er zelf werkgeverslasten, verzekeringen en pensioenopbouw draagt. Houd er rekening mee dat overheden sinds de aangescherpte DBA-handhaving steeds vaker expliciet voor detachering kiezen, ook bij hogere tarieven.",
      },
      {
        h: "Voorbeeldopdrachten",
        p:
          "Gemeente Kampen, beleidsadviseur: € 125/u (12 u/wk). Ministerie van EZ, technical expert infra: € 130/u (geen max). Gemeente Utrecht, beleidsadviseur woonruimteverdeling: € 120/u (36 u/wk). Gemeente Rheden, consulent Wpi: € 85/u (24 u/wk).",
      },
    ],
  },
];

export default function Kenniscentrum() {
  const [openArticle, setOpenArticle] = useState(null);
  return (
    <>
      <section className="bg-[#f7f6f2]">
        <div className="ok-container py-20 lg:py-24">
          <div className="ok-eyebrow mb-3">Kenniscentrum</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
            Inzichten voor de publieke sector.
          </h1>
          <p className="mt-6 text-lg text-[#4a5d5e] max-w-2xl leading-relaxed">
            Artikelen, checklists en marktinzichten voor professionals en overheidsorganisaties.
            Van Omgevingswet tot Flextender, van AVG tot tariefbenchmarks.
          </p>
        </div>
      </section>

      <section className="ok-section">
        <div className="ok-container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ARTICLES.map((a, i) => (
              <button
                key={i}
                data-testid={`kc-article-${i}`}
                onClick={() => setOpenArticle(a)}
                className="ok-card p-6 group cursor-pointer text-left"
              >
                <a.icon size={22} className="text-[#01696f]" />
                <div className="ok-chip mt-5">{a.category}</div>
                <h3 className="font-display text-xl font-semibold mt-3 leading-snug group-hover:text-[#01696f] transition-colors">
                  {a.title}
                </h3>
                <p className="text-sm text-[#4a5d5e] mt-3 leading-relaxed">{a.excerpt}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="ok-link-arrow text-sm">Lees verder →</span>
                  <span className="text-xs text-[#7b8e8f]">{a.readTime}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-16 border border-[#e2e8f0] p-8 md:p-10 bg-[#f7f6f2] flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl font-semibold tracking-tight">
                Vraag het onze recruiters direct.
              </h3>
              <p className="text-sm text-[#4a5d5e] mt-2 max-w-xl">
                Heb je een specifieke vraag over een opdracht, tarief of marktontwikkeling? Onze recruiters helpen je graag verder.
              </p>
            </div>
            <Link
              to="/aanmelden-inlener"
              className="inline-flex items-center gap-2 bg-[#01696f] text-white px-6 py-3 rounded-sm hover:bg-[#015257] font-medium whitespace-nowrap"
            >
              Neem contact op <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {openArticle && <ArticleModal article={openArticle} onClose={() => setOpenArticle(null)} />}
    </>
  );
}

function ArticleModal({ article, onClose }) {
  const Icon = article.icon;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="kc-article-modal"
    >
      <div
        className="bg-white max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto border border-[#e2e8f0] rounded-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#e2e8f0] px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Icon size={18} className="text-[#01696f]" />
            <span className="ok-chip">{article.category}</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#f7f6f2] rounded-sm" aria-label="Sluiten" data-testid="kc-article-close">
            <X size={18} />
          </button>
        </div>
        <article className="p-6 md:p-10">
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
            {article.title}
          </h1>
          <p className="text-xs text-[#7b8e8f] mt-3">{article.readTime}</p>
          <p className="mt-6 text-lg text-[#4a5d5e] leading-relaxed">{article.excerpt}</p>
          <div className="mt-10 space-y-8">
            {article.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-display text-xl md:text-2xl font-semibold mb-3">{s.h}</h2>
                <p className="text-[#0a191a] leading-relaxed">{s.p}</p>
              </section>
            ))}
          </div>
          <div className="mt-12 border-t border-[#e2e8f0] pt-6 text-sm text-[#4a5d5e]">
            Meer weten? Neem contact op via{" "}
            <a href="mailto:info@overheidskrachten.nl" className="text-[#01696f] font-semibold hover:underline">
              info@overheidskrachten.nl
            </a>
          </div>
        </article>
      </div>
    </div>
  );
}
